import { Trophy, TrendingUp, TrendingDown, Target, Percent, BarChart3 } from "lucide-react";
import { fmtUsd } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";

const REASON_STYLE = {
  "Kâr Al": "text-[var(--pos)] bg-[#00FFA3]/10",
  "Stop-Loss": "text-[var(--neg)] bg-[#FF3366]/10",
  "Sinyal Kapanış": "text-[var(--accent)] bg-[#A374FF]/10",
  "Sinyal (Manuel)": "text-[var(--text-dim)] bg-[#A1A1AA]/10",
};

const Stat = ({ label, value, sub, positive, testid }) => (
  <div data-testid={testid} className="rounded-md border border-[var(--border)] bg-[var(--surface)] p-3">
    <div className="text-[10px] text-[var(--text-faint)] uppercase tracking-wider">{label}</div>
    <div className={cn("font-mono font-bold text-lg mt-1",
      positive === undefined ? "text-[var(--text-primary)]" : positive ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
      {value}
    </div>
    {sub !== undefined && <div className="text-[10px] text-[var(--text-faint)] mt-0.5">{sub}</div>}
  </div>
);

export const BotPerformancePanel = ({ perf }) => {
  const { isLight } = useTheme();
  if (!perf) return null;
  const pnlPos = perf.total_realized_pnl >= 0;
  const wr = perf.win_rate;
  const trackStroke = isLight ? "#E4E4E7" : "#1A1A1D";
  const wrStroke = wr >= 50 ? (isLight ? "#00B877" : "#00FFA3") : (isLight ? "#E11D48" : "#FF3366");
  return (
    <div data-testid="bot-performance-panel" className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)]">
      <div className="flex items-center gap-2.5 px-5 h-14 border-b border-[var(--border)]">
        <div className="h-8 w-8 rounded-md bg-[#A374FF]/15 border border-[#A374FF]/30 flex items-center justify-center">
          <BarChart3 className="h-4 w-4 text-[var(--accent)]" />
        </div>
        <h3 className="font-head font-bold text-base">Bot Performans Raporu</h3>
      </div>

      <div className="p-5 space-y-4">
        <p data-testid="performance-scope" className="text-xs text-[var(--text-faint)]">{perf.live ? "Canlı" : "Simülasyon"} · son 500 satış kaydı · açık pozisyonlar hariç</p>
        {perf.unpriced_trades > 0 && <p data-testid="performance-fee-warning" role="alert" className="text-xs text-[var(--warn)]">{perf.unpriced_trades} satışın ücreti henüz doğrulanmadı. Toplam ve ortalama K/Z kesinleşmedi.</p>}
        {perf.pnl_excludes_funding && <p data-testid="performance-funding-warning" className="text-xs text-[var(--warn)]">Vadeli sonuçlarda fonlama ödemeleri dahil değildir.</p>}
        {perf.closed_trades === 0 ? (
          <div className="text-center py-8 text-[var(--text-faint)]">
            <Trophy className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Henüz kapanmış işlem yok.</p>
            <p className="text-xs mt-1">Bot işlem kapattıkça performans burada görünür.</p>
          </div>
        ) : (
          <>
            {/* success rate hero */}
            <div className="flex items-center gap-4 rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4">
              <div className="relative h-20 w-20 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                  <path d="M18 2 a16 16 0 0 1 0 32 a16 16 0 0 1 0 -32" fill="none" stroke={trackStroke} strokeWidth="3.5" />
                  <path d="M18 2 a16 16 0 0 1 0 32 a16 16 0 0 1 0 -32" fill="none"
                    stroke={wrStroke} strokeWidth="3.5"
                    strokeDasharray={`${wr}, 100`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span data-testid="win-rate" className="font-mono font-bold text-lg leading-none">{wr}%</span>
                  <span className="text-[9px] text-[var(--text-faint)]">başarı</span>
                </div>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div className="flex items-center gap-1.5 text-sm"><TrendingUp className="h-4 w-4 text-[var(--pos)]" />
                  <span className="font-mono font-bold text-[var(--pos)]">{perf.wins}</span>
                  <span className="text-[11px] text-[var(--text-faint)]">kazanç</span></div>
                <div className="flex items-center gap-1.5 text-sm"><TrendingDown className="h-4 w-4 text-[var(--neg)]" />
                  <span className="font-mono font-bold text-[var(--neg)]">{perf.losses}</span>
                  <span className="text-[11px] text-[var(--text-faint)]">zarar</span></div>
                <div className="col-span-2 text-[11px] text-[var(--text-faint)]">
                  Toplam {perf.closed_trades} kapanmış işlem
                </div>
              </div>
            </div>

            {/* stat grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Stat testid="perf-total-pnl" label="Realize K/Z"
                value={perf.unpriced_trades > 0 ? "Bekleniyor" : `${pnlPos ? "+" : ""}${fmtUsd(perf.total_realized_pnl, 4)}`} positive={pnlPos} />
              <Stat testid="perf-avg-pnl" label="Ort. İşlem"
                value={perf.unpriced_trades > 0 ? "Bekleniyor" : `${perf.avg_pnl >= 0 ? "+" : ""}${fmtUsd(perf.avg_pnl, 4)}`} positive={perf.avg_pnl >= 0} />
              <Stat testid="perf-best" label="En İyi"
                value={perf.best ? `+${fmtUsd(perf.best.pnl)}` : "--"}
                sub={perf.best?.symbol} positive />
            </div>

            {/* breakdown by reason */}
            {perf.by_reason?.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-[11px] text-[var(--text-faint)] uppercase tracking-wider flex items-center gap-1">
                  <Target className="h-3 w-3" /> Kapanış Türüne Göre
                </div>
                {perf.by_reason.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={cn("px-2 py-0.5 rounded font-mono", REASON_STYLE[r.label] || "text-[var(--text-dim)] bg-[var(--surface-hover)]")}>
                      {r.label}
                    </span>
                    <span className="text-[var(--text-faint)]">{r.count} işlem</span>
                    <span className={cn("ml-auto font-mono", r.pnl >= 0 ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
                      {r.pnl >= 0 ? "+" : ""}{fmtUsd(r.pnl)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
